import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from './domain';

const dictionaries = {
	ru: {
		brandLine: 'Закупки в Китае', navHow: 'Как это работает', navBenefits: 'Возможности', navWorkspace: 'Личный кабинет', navFaq: 'Вопросы', login: 'Войти', start: 'Создать заявку', support: 'Помощь',
		heroKicker: 'Для продавцов маркетплейсов', heroTitleA: 'Товар из Китая —', heroTitleB: 'без хаоса',
		heroText: 'Пришлите ссылку, фото или просто опишите задачу. Мы найдём варианты, посчитаем полную стоимость и довезём до выдачи.',
		heroPrimary: 'Рассчитать закупку', heroSecondary: 'Посмотреть процесс', trustOne: 'Одна заявка вместо десятка чатов', trustTwo: 'Стоимость понятна до оплаты',
		cabinet: 'Кабинет', requests: 'Заявки', newRequest: 'Новая заявка', notifications: 'Уведомления', profile: 'Профиль', logout: 'Выйти',
		admin: 'Админка', queue: 'Рабочая очередь', clients: 'Клиенты', team: 'Команда', roles: 'Роли и права', approvals: 'Апрувы', settings: 'Настройки',
	},
	ua: {
		brandLine: 'Закупівлі в Китаї', navHow: 'Як це працює', navBenefits: 'Можливості', navWorkspace: 'Особистий кабінет', navFaq: 'Питання', login: 'Увійти', start: 'Створити заявку', support: 'Допомога',
		heroKicker: 'Для продавців маркетплейсів', heroTitleA: 'Товар із Китаю —', heroTitleB: 'без хаосу',
		heroText: 'Надішліть посилання, фото або просто опишіть завдання. Ми знайдемо варіанти, порахуємо повну вартість і доставимо до видачі.',
		heroPrimary: 'Розрахувати закупівлю', heroSecondary: 'Переглянути процес', trustOne: 'Одна заявка замість десятка чатів', trustTwo: 'Вартість зрозуміла до оплати',
		cabinet: 'Кабінет', requests: 'Заявки', newRequest: 'Нова заявка', notifications: 'Сповіщення', profile: 'Профіль', logout: 'Вийти',
		admin: 'Адмінка', queue: 'Робоча черга', clients: 'Клієнти', team: 'Команда', roles: 'Ролі та права', approvals: 'Погодження', settings: 'Налаштування',
	},
	en: {
		brandLine: 'Sourcing from China', navHow: 'How it works', navBenefits: 'Capabilities', navWorkspace: 'Workspace', navFaq: 'FAQ', login: 'Sign in', start: 'Create request', support: 'Help',
		heroKicker: 'For marketplace sellers', heroTitleA: 'Goods from China —', heroTitleB: 'without the chaos',
		heroText: 'Send a link, photo, or simply describe the task. We will find options, calculate the landed cost, and deliver to pickup.',
		heroPrimary: 'Calculate sourcing', heroSecondary: 'See the process', trustOne: 'One request instead of ten chats', trustTwo: 'Clear cost before payment',
		cabinet: 'Workspace', requests: 'Requests', newRequest: 'New request', notifications: 'Notifications', profile: 'Profile', logout: 'Sign out',
		admin: 'Admin', queue: 'Work queue', clients: 'Clients', team: 'Team', roles: 'Roles & permissions', approvals: 'Approvals', settings: 'Settings',
	},
} as const;

type TranslationKey = keyof typeof dictionaries.ru;

interface I18nContextValue {
	locale: Locale;
	setLocale: (locale: Locale) => void;
	t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
	const [locale, setLocaleState] = useState<Locale>(() => {
		const saved = localStorage.getItem('volt-locale') as Locale | null;
		return saved && ['ua', 'ru', 'en'].includes(saved) ? saved : 'ru';
	});
	useEffect(() => {
		document.documentElement.lang = locale;
		localStorage.setItem('volt-locale', locale);
	}, [locale]);
	const value = useMemo<I18nContextValue>(() => ({
		locale,
		setLocale: setLocaleState,
		t: (key) => dictionaries[locale][key],
	}), [locale]);
	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
	const value = useContext(I18nContext);
	if (!value) throw new Error('useI18n must be used inside I18nProvider');
	return value;
}
